import { motion } from "framer-motion";
import { Globe2, Ship, PackageCheck, Stamp } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const REGIONS = [
    {
        name: "Americas",
        detail: "USA · Canada · Latin America",
        text: "Retail, corporate gifting and promotional buyers. Prop 65-aware production and consolidated sea or air freight.",
    },
    {
        name: "Europe & UK",
        detail: "EU · United Kingdom · Nordics",
        text: "Boutique and department-store programs. REACH-conscious materials, bespoke packaging and seasonal drops.",
    },
    {
        name: "Australia & Oceania",
        detail: "Australia · New Zealand",
        text: "Heritage leather and souvenir lines for gift, travel and lifestyle retail across the region.",
    },
];

const ASSURANCES = [
    { icon: PackageCheck, label: "MOQ from 100 units" },
    { icon: Stamp, label: "Custom embossing & private label" },
    { icon: Ship, label: "FOB / CIF — sea & air freight" },
    { icon: Globe2, label: "Mixed-SKU sample kits" },
];

export const Markets = ({ onNavigate }) => (
    <section id="markets" data-testid="markets-section" className="relative py-24 sm:py-32 bg-[#0F0C08]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: EASE }}
                className="text-center mb-16"
            >
                <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4">
                    Global Markets
                </p>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#F4EFEA] tracking-tight">
                    Built for your <em className="italic text-[#D4A373]">shelf</em>, wherever it is.
                </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-5">
                {REGIONS.map((r, i) => (
                    <motion.div
                        key={r.name}
                        data-testid={`market-card-${i}`}
                        initial={{ opacity: 0, y: 36 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
                        className="group relative rounded-md border border-[#2E2620] bg-[#1D1814] p-8 hover:border-[#C58B49]/50 hover:-translate-y-1.5 transition-all duration-500"
                    >
                        <span className="font-mono2 text-[10px] tracking-[0.3em] text-[#C58B49] uppercase">
                            {r.detail}
                        </span>
                        <h3 className="mt-4 font-display text-3xl sm:text-4xl font-semibold text-[#F4EFEA]">
                            {r.name}
                        </h3>
                        <p className="mt-4 text-sm text-[#8C8075] leading-relaxed">{r.text}</p>
                        <div className="mt-6 h-px bg-gradient-to-r from-[#C58B49]/50 to-transparent" />
                        <button
                            data-testid={`market-enquire-${i}`}
                            onClick={() => onNavigate("enquiry")}
                            className="mt-5 text-[12px] tracking-[0.18em] uppercase text-[#D4A373] hover:text-[#F4EFEA] transition-colors"
                        >
                            Discuss this market →
                        </button>
                    </motion.div>
                ))}
            </div>

            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                className="mt-10 rounded-md border border-[#2E2620] bg-[#14110F] px-6 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6"
                data-testid="export-assurances"
            >
                {ASSURANCES.map((a) => (
                    <div key={a.label} className="flex items-center gap-3">
                        <a.icon size={18} strokeWidth={1.6} className="text-[#C58B49] shrink-0" />
                        <span className="text-[13px] text-[#C2B7AD] leading-snug">{a.label}</span>
                    </div>
                ))}
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
                className="mt-10 text-center"
            >
                <a
                    data-testid="download-catalog-btn"
                    href="/assets/fnb-catalog.pdf"
                    download
                    className="inline-flex items-center gap-2 px-8 py-3.5 border border-[#C58B49]/60 text-[#D4A373] text-[13px] tracking-[0.16em] uppercase rounded-sm hover:bg-[#C58B49] hover:text-[#0F0C08] transition-colors duration-300"
                >
                    Download Full Catalog (PDF)
                </a>
            </motion.div>
        </div>
    </section>
);
